import { mode } from '@chakra-ui/theme-tools';
import { extendTheme } from '@chakra-ui/react';
import components from './components';
import colors from './colors';
import rcTreeStyles from './components/rcTree.styles';

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  fonts: {
    heading: 'Poppins',
    body: 'Poppins',
  },
  fontWeights: {
    bold: 600,
  },
  radii: {
    xs: '0.125rem',
    sm: '0.25rem',
  },
  colors: {
    ...colors,
    WPPGreen: {
      300: '#B0F467',
      400: '#9edb5c',
    },
    WPPNavy: {
      200: '#000069',
      300: '#000050',
      400: '#000035',
    },
    WPPTeal: {
      300: '#93DFE3',
      400: '#83c6c9',
    },
    WPPOrange: {
      200: '#ff5444',
      300: '#e54b3d',
      400: '#cc4337',
    },

    WPPSecondaryYellow: {
      100: '#FCFE67',
    },
    WPPSecondaryTeal: {
      100: '#00DBEE',
    },
    WPPgray: {
      100: 'rgba(145, 145, 177, 0.6)',
      200: '#9191B1',
    },
    nexusTeal: {
      100: '#00B5B1',
      200: '#009c99',
    },
    nexusBlue: {
      100: '#0073e5',
      200: '#0080FF',
      300: '#0A2756',
    },

    nexusMint: {
      100: '#99FDD1',
      200: '#88e3bc',
      300: '#61f2c7',
      400: '#57d9b2',
      500: '#3d997d',
    },
    nexusLightTeal: {
      100: '#B2E9E7',
    },
    nexusElectricBeige: {
      100: '#FFEDCA',
    },
    nexusYellow: {
      100: '#f9e5b2',
      200: '#f7de9a',
      300: '#f5d683',
      400: '#F3CE6B',
      500: '#f1c653',
    },
    graphite: {
      100: 'rgba(49,50,51,0.05)',
      200: 'rgba(49,50,51,0.1)',
      300: 'rgba(49,50,51,0.25)',
      400: 'rgba(49,50,51,0.5)',
      500: '#313233',
      600: '#4a4b4d',
      700: '#636366',
    },
    white: {
      100: '#fff',
      200: '#F2F2F2 ',
    },
    dark: {
      100: '#101122',
      200: '#000019',
      300: '#e3e7f0',
    },
    selected: 'rgba(18, 22, 77, 0.1)',
    primary: '#faa002',
    secondary: '#fff',
    navyBlue: {
      200: '#414470',
      300: '#292D5E',
      400: '#12164d',
      500: '#101345',
      600: '#0E113D',
      800: '#0A0D2E',
    },
    avatar: {
      bg: '#fedbeb',
      text: '#8f4567',
    },
    greenLight: '#D7F3BA',
    blueLight: '#F7FAFC',
  },
  styles: {
    global: props => ({
      'html, body': {
        bg: mode('white.200', 'dark.200')(props),
        color: mode('graphite.500', 'dark.300')(props),
      },
      '*': {
        scrollbarWidth: 'auto',
        scrollbarColor: 'transparent',
      },

      '*::-webkit-scrollbar': {
        width: '12px',
      },

      '*::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '*::-webkit-scrollbar-corner': { background: 'rgba(0, 0, 0, 0)' },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: mode('graphite.400', 'graphite.600')(props),
        borderRadius: '10px',
        borderWidth: '3px',
        borderColor: mode('white.200', 'dark.200')(props),
        borderStyle: 'solid',
      },
      'input::-webkit-datetime-edit': {
        cursor: 'text',
      },
      'input::-webkit-calendar-picker-indicator': {
        cursor: 'pointer',
        position: 'absolute',
        right: '.25em',
        opacity: 0.6,
        filter: mode('invert(0)', 'invert(1)')(props),
      },
      '.recharts-surface': {
        overflow: 'visible',
      },
      '.recharts-cartesian-grid line': {
        stroke: mode('graphite.300', 'graphite.600')(props),
      },
      '.recharts-cartesian-axis line': {
        stroke: mode('graphite.300', 'graphite.600')(props),
      },
      '.recharts-cartesian-axis text': {
        fill: mode('graphite.400', 'graphite.600')(props),
        fontSize: 'xs',
        fontWeight: 'bold',
      },
      '.recharts-legend-item-text': {
        fontSize: 'md',
        fontWeight: 'bold',
        color: mode('graphite.400', 'graphite.600')(props),
      },
      '.recharts-tooltip-cursor': {
        stroke: mode('graphite.300', 'graphite.600')(props),
      },
      '.recharts-line path': {
        strokeWidth: 3,
      },
      '.recharts-line-dots circle': {
        strokeWidth: 3,
        fill: mode('graphite.50', 'graphite.800')(props),
      },
      ...rcTreeStyles(props),
    }),
  },
  shadows: {
    base: '0px 5px 20px 0px rgba(49, 49, 77, 0.05)',
  },
  layerStyles: {
    rowCardBordered: {
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: 'gray.200',
      borderRadius: 'md',
      px: 4,
      py: 3,
      _dark: { bg: '#1A202C', borderColor: 'white.100', color: 'white.100' },
    },
    rowCardFilled: {
      alignItems: 'center',
      justifyContent: 'space-between',
      bg: 'gray.100',
      borderRadius: 'md',
      borderWidth: 1,
      px: 4,
      py: 2,
      _dark: { bg: '#1A202C', borderColor: 'white.100', color: 'white.100' },
    },
    rowCardStatus: {
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 4,
      bg: 'gray.50',
      borderWidth: 1,
      borderRadius: 'md',
      px: 4,
      py: 3,
      _dark: { bg: '#1A202C', borderColor: 'white.100', color: 'white.100' },
    },
  },
  components,
});

export default theme;
